
export enum UserRole {
  WEBADMIN = 'WEBADMIN',
  ADMIN = 'ADMIN', // Ligaleitung
  STEWARD = 'STEWARD',
  DRIVER = 'DRIVER',
  CASTER = 'CASTER'
}

export interface User {
  id: string;
  username: string;
  eaId?: string;
  roles: UserRole[];
  teamId?: string; // Current season team
  seasonDriverId?: string; // Check if participating in current season
  is_active?: boolean; // Ban status
  has_telemetry_access?: boolean; // Special permission
  avatarUrl?: string; // Profile Picture
  twitch_name?: string;
  youtube_handle?: string;
  kick_name?: string;
  discord_id?: string; // NEW: For Pings
  rulebook_accepted_at?: string | null; // Timestamp when rules were accepted
  last_login_at?: string | null;
  is_placeholder?: boolean;
  teamName?: string;
  teamColor?: string;
}

export interface Team {
  id: string;
  name: string;
  color: string;
  logoUrl?: string;
  car_image_url?: string;
}

export interface Track {
  id: string;
  name: string;
  location: string;
  flagUrl?: string;
  layoutUrl?: string;
}

export enum SeasonStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  FINISHED = 'FINISHED'
}

export interface Season {
  id: string;
  name: string;
  year: number;
  status: SeasonStatus;
  is_active?: boolean;
  event_count?: number;
  team_count?: number;
}

export enum EventStatus {
  UPCOMING = 'UPCOMING',
  OPEN_FOR_REGISTRATION = 'OPEN_FOR_REGISTRATION',
  REGISTRATION_CLOSED = 'REGISTRATION_CLOSED',
  COMPLETED = 'COMPLETED'
}

export interface RaceEvent {
  id: string;
  seasonId: string;
  trackId: string;
  date: string; // ISO string
  isSprint: boolean;
  isTestRace?: boolean; // Test Race Flag
  distance: '50%' | '100%';
  status: EventStatus;
  track?: Track; // Hydrated
}

export enum AttendanceStatus {
  UNKNOWN = 'UNKNOWN',
  ATTENDING = 'ATTENDING',
  ABSENT = 'ABSENT'
}

export enum TicketType {
  PROTEST = 'PROTEST',
  DNF = 'DNF',
  INCIDENT = 'INCIDENT'
}

export enum TicketStatus {
  OPEN_PUBLIC = 'OPEN_PUBLIC',
  AWAITING_STATEMENT = 'AWAITING_STATEMENT',
  IN_REVIEW_PRIVATE = 'IN_REVIEW_PRIVATE',
  DECISION_PENDING = 'DECISION_PENDING',
  PUBLISHED = 'PUBLISHED',
  CLOSED = 'CLOSED'
}

export interface Ticket {
  id: string;
  eventId: string;
  authorId: string;
  accusedId?: string; // Driver ID
  type: TicketType;
  status: TicketStatus;
  lap?: number;
  corner?: string;
  description: string;
  videoUrl?: string;
  createdAt: string;
}

export interface NewsItem {
  id: number;
  title: string;
  content: string;
  category: 'OFFICIAL' | 'DECISION' | 'ANNOUNCEMENT';
  author_name: string;
  created_at: string;
  related_ticket_id?: number;
  published_at?: string;
  is_scheduled?: boolean;
  event_id?: string;
  track_location?: string;
}

export interface LeagueSettings {
    point_fastest_lap: number;
    point_dotd: number;
    protest_deadline_hours: number;
    penalty_points_ban: number;
    upload_limit_mb: number;
    dotd_window_hours: number;
    attendance_strikes_limit: number;
    theme_color: string;
    league_name: string;
    league_logo_url: string | null;
    reserve_logo_url: string | null;
    twitch_channel: string | null;
    discord_webhook_url: string | null; 
    news_webhook_url: string | null; // NEW: Specific for News Generator
    points_50: number[];
    points_100: number[];
    points_sprint: number[];
    dotd_badge_id?: string | number | null;
    penalties_apply_to_team?: boolean;
    clean_race_counts_for_team?: boolean; // NEW
    rulebook_url?: string | null;
    rulebook_updated_at?: string | null;
    // Prediction Game Settings
    prediction_points_exact?: number;
    prediction_points_top10?: number;
    prediction_points_full_bonus?: number;
    prediction_points_sprint_exact?: number;
    prediction_points_sprint_top3?: number;
}

export interface ResultRow {
    user_id: string;
    username: string;
    team_id: string;
    teamName: string;
    teamColor?: string;
    position: string | number; // Race Pos
    quali_position?: string | number;
    sprint_position?: string | number;
    status: 'FINISHED' | 'DNF' | 'DNS' | 'DSQ';
    quali_status?: string;
    sprint_status?: string;
    fastest_lap: boolean;
    points?: number;
    sprint_points?: number;
    telemetry_data?: any; // Stores laps, stints, etc.
}

export interface RuleItem {
    db_id?: number; // Database Primary Key
    id: string; // The rule key e.g. "3.7"
    label: string; // Description
    points: number | null; // null means variable/manual
    extra: string[];
}

export interface RuleGroup {
    group: string;
    items: RuleItem[];
}

export interface PenaltyRecord {
    id: number;
    ticket_id: number;
    user_id: string;
    username?: string;
    rule_id: string; // e.g. "8.4.2"
    points: number; // Penalty points
    time_penalty: number; // Seconds
    grid_penalty: number; // Positions
    warning: boolean;
    description: string;
    created_at: string;
    event_name?: string;
    track_location?: string;
}

export interface DotDCandidate {
    driver_id: string;
    username: string;
    team_color: string;
    team_logo?: string; // Added for graphic
    votes: number;
}

export interface BugReport {
    id: number;
    user_id: string;
    reporter_name: string;
    description: string;
    priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    status: 'OPEN' | 'IN_PROGRESS' | 'DONE';
    created_at: string;
}
